import { Helmet } from "react-helmet-async";

export default function FitterAITerms() {
  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#26313B] p-8 md:p-16 transition-colors duration-300">
      <Helmet>
        <title>Terms of Service – FitterAI</title>
        <meta
          name="description"
          content="Terms of Service and EULA for FitterAI, the AI-powered fitness coach by Cosmovex."
        />
        <link
          rel="canonical"
          href="https://cosmovexapps.com/apps/fitterai/terms"
        />
      </Helmet>

      <h1 className="text-4xl font-bold mb-6 text-[#A8720E]">
        Terms of Service – FitterAI
      </h1>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        By using <strong>FitterAI</strong>, you agree to these Terms of Service.
        FitterAI is developed and operated by <strong>Cosmovex</strong>. If you
        do not agree with these terms, please do not use the app.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">1. Service Description</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        FitterAI is an AI-powered fitness application that provides personalized
        workout plans, voice coaching, exercise form analysis, progress tracking,
        and nutritional guidance. The app uses artificial intelligence to generate
        content and recommendations tailored to your fitness goals.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">2. AI-Generated Content Disclaimer</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        Workout plans, form analysis, coaching advice, nutrition suggestions, and
        body transformation predictions are generated by AI and are provided for
        informational purposes only. They are <strong>not medical advice</strong>.
        Always consult a qualified healthcare professional before starting any
        exercise program, especially if you have pre-existing health conditions,
        injuries, or concerns. You use AI-generated workout plans and advice at
        your own risk.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">3. Subscriptions</h2>

      <ul className="list-disc list-inside text-[#3D4B59] leading-relaxed space-y-2 max-w-4xl">
        <li>FitterAI Pro is available as a monthly ($6.99/mo) or annual ($39.99/yr) subscription.</li>
        <li>Subscriptions auto-renew unless cancelled at least 24 hours before the current period ends.</li>
        <li>Payment is charged to your Apple ID or Google Play account at confirmation of purchase.</li>
        <li>You can manage or cancel subscriptions in your device&apos;s store settings.</li>
        <li>The annual plan includes a 7-day free trial. If cancelled during the trial, you will not be charged.</li>
        <li>Refunds are handled by Apple or Google according to their respective refund policies.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">4. User Content</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        You retain ownership of content you create within FitterAI, including
        progress photos, workout logs, and profile information. By using sharing
        features, you grant Cosmovex a non-exclusive license to display your
        shared content with FitterAI branding (e.g., workout summary cards with
        watermarks). You are responsible for ensuring any content you share does
        not violate others&apos; rights.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">5. Acceptable Use</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-4 max-w-4xl">
        You agree not to:
      </p>

      <ul className="list-disc list-inside text-[#3D4B59] leading-relaxed space-y-2 max-w-4xl">
        <li>Use FitterAI to harm, harass, or abuse other users.</li>
        <li>Create fake accounts or abuse the referral program.</li>
        <li>Attempt to reverse-engineer, decompile, or extract the AI models or proprietary algorithms.</li>
        <li>Use automated tools to scrape data from the app or its services.</li>
        <li>Upload inappropriate, offensive, or illegal content as progress photos.</li>
      </ul>

      <h2 className="text-2xl font-semibold mt-10 mb-4">6. Account Termination</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        We reserve the right to suspend or terminate accounts that violate these
        terms, abuse the referral system, engage in fraudulent activity, or
        otherwise harm the FitterAI community. You may delete your account at any
        time through the app settings.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">7. Limitation of Liability</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        FitterAI is provided &ldquo;as is&rdquo; without warranties of any kind.
        Cosmovex shall not be liable for any injuries, health issues, or damages
        arising from the use of AI-generated workout plans, form analysis, or
        nutritional advice. You acknowledge that fitness activities carry inherent
        risks and you exercise at your own discretion.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">8. Changes to Terms</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        We may update these Terms from time to time. Continued use of FitterAI
        after changes constitutes acceptance of the updated terms.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">9. Contact</h2>

      <p className="text-[#3D4B59] leading-relaxed max-w-4xl">
        For questions about these Terms, contact{" "}
        <a
          href="mailto:support@cosmovex.com"
          className="text-[#0F6BBF] underline hover:text-[#0C5AA3]"
        >
          support@cosmovex.com
        </a>
        .
      </p>

      <p className="text-[#8A93A2] mt-10">
        Last updated: March 19, 2026
      </p>
    </div>
  );
}
